import React, { FunctionComponent, useState } from 'react';
import { ArrowBackIos } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Paper,
  Modal,
  Checkbox,
  FormControlLabel,
  Grid
} from '@mui/material';
import JsonView from '@uiw/react-json-view';
import { Header } from '../../components/header/header';
import { ContainerStyled } from '../../components/layout/main/mainStyles';
import {
  ButtonIconStyled,
  TitleStyled
} from '../locations/allLocationsList/allLocationListStyles';
import Strings from '../../i18n';
import { useTestDeviceMutation } from '../../services/device/deviceService';

const COMMAND_NEW_GEN =
  '{"methods": ["<LIGHTING.ON({\\"CH\\": [1,2,3,4], \\"FUNCTION\\": \\"HSV\\", \\"CONFIG\\": {\\"HUE\\": 0, \\"SAT\\": 100, \\"VAL\\": 100.00}})>"]}';
const COMMAND_OLD_GEN =
  '{"Command": {"Name":"Play","Parameters":{"Data":"SEEUAAAATQABPwEePwAAABQADQo="}}}';

interface TestControllerProps {}

export const TestController: FunctionComponent<TestControllerProps> = () => {
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const { state } = useLocation();
  const isOldGeneration = state?.isOldGeneration ?? false;

  const [testDevice, { isLoading, isSuccess, data }] = useTestDeviceMutation();
  const [openModal, setOpenModal] = useState(false);

  const [totalRequests, setTotalRequests] = useState<number>(1);
  const [payload, setPayload] = useState<string>(
    isOldGeneration ? COMMAND_OLD_GEN : COMMAND_NEW_GEN
  );
  const [methodName, setMethodName] = useState<string>(
    isOldGeneration ? 'SendCommandMethod' : 'ExecuteCommand'
  );
  const [waitForResponse, setWaitForResponse] = useState<boolean>(true);
  const [delay, setDelay] = useState<number>(0);

  const handleSendRequest = () => {
    testDevice({
      DeviceId: deviceId || '',
      MethodName: methodName,
      Payload: payload,
      TotalRequests: totalRequests,
      WaitForResponse: waitForResponse,
      Delay: delay
    });
  };

  return (
    <>
      <Header>
        <ContainerStyled>
          <div className="row">
            <div className="col-4 text-left">
              <ButtonIconStyled size="small" onClick={() => navigate(-1)}>
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-4 text-center">
              <TitleStyled>
                {Strings.serverAccess.devices.testController}
              </TitleStyled>
            </div>
          </div>
        </ContainerStyled>
      </Header>

      <Box padding={4} display="flex" justifyContent="center">
        <Box width="100%">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Total Requests"
                type="number"
                fullWidth
                value={totalRequests}
                onChange={(e) => setTotalRequests(Number(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                label="Method Name"
                fullWidth
                value={methodName}
                onChange={(e) => setMethodName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={3} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={waitForResponse}
                    onChange={(e) => setWaitForResponse(e.target.checked)}
                  />
                }
                label="Wait For Response"
              />
            </Grid>

            {!waitForResponse && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Delay (ms)"
                  type="number"
                  fullWidth
                  value={delay}
                  onChange={(e) => setDelay(Number(e.target.value))}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Payload"
                fullWidth
                multiline
                minRows={6}
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSendRequest}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={26} />
                ) : (
                  Strings.serverAccess.devices.testController
                )}
              </Button>
            </Grid>

            {isSuccess && (
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  onClick={() => setOpenModal(true)}
                >
                  Show Results
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Paper
            style={{
              width: '80%',
              maxHeight: '80%',
              margin: '50px auto',
              padding: 20,
              overflow: 'auto'
            }}
          >
            <h3>Response Data</h3>
            <JsonView
              value={data}
              displayDataTypes={false}
              displayObjectSize={false}
              indentWidth={2}
              shortenTextAfterLength={1000}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(false)}
              style={{ marginTop: 20 }}
            >
              Close
            </Button>
          </Paper>
        </Modal>
      </Box>
    </>
  );
};
